import defaultMetrics from '@/organisms/plans/utils/defaultMetrics';
import ActionType from '@/shared/enums/ActionType';
import EntityType from '@/shared/enums/EntityType';
import entityLabel from '@/shared/utils/entityLabel';
import defaultMetricGroup from '@pkg/entities/snapshots/utils/defaultMetricGroup';

/**
 * Adds new roles to the actions map.
 *
 * @param {Object}
 *
 * @return {Object}
 */
export default function addedRolesAction({
  actionMap,
  comparisonScenario,
  role,
  scenario,
}) {
  const group = role
    ? scenario?.relationships.get(EntityType.GROUP)?.get(role.group_uuid)
    : null;

  const metrics = role.__metrics.self.visible;

  const existingAction = actionMap?.get(ActionType.ADD_ROLES);

  const action = existingAction ?? {
    entity: EntityType.ROLE,
    metrics: defaultMetrics({ comparisonScenario }),
    impact: defaultMetricGroup(),
    type: ActionType.ADD_ROLES,
    [EntityType.ROLE]: new Map(),
    [EntityType.GROUP]: new Set(),
  };

  if (group) {
    action[EntityType.GROUP].add(group?.uuid);
  }

  action.metrics[1].activities += metrics.activities;
  action.metrics[1].budget += metrics.budget;
  action.metrics[1].allocatedFTE += metrics.allocatedFTE;
  action.metrics[1].fte += metrics.fte;
  action.metrics[1].hours += metrics.hours;
  action.metrics[1].roles += 1;

  // Impact changes.
  action.impact.budget += metrics.budget;
  action.impact.allocatedFTE += metrics.allocatedFTE;
  action.impact.fte += metrics.fte;
  action.impact.hours += metrics.hours;

  // Aggregate the individual role action metrics.
  const actionRole = action[EntityType.ROLE].get(role.uuid) ?? {
    impact: defaultMetricGroup(),
    metrics: defaultMetrics({ comparisonScenario, role }),
  };

  const roleProperties = {};

  // Add the parent manager.
  if (role.__manager) {
    roleProperties.moved = {
      manager: {
        to: role.__manager,
      },
    };
  }

  // Add the parent group.
  if (role.group_uuid) {
    roleProperties.moved = {
      ...roleProperties.moved,
      group: {
        to: role.group_uuid,
      },
    };
  }

  // Add the assignee.
  roleProperties.assigned = {
    to: role.person_uuid || null,
  };

  actionRole.props = { ...roleProperties };

  actionRole.metrics[1].activities += metrics.activities;
  actionRole.metrics[1].budget += metrics.budget;
  actionRole.metrics[1].allocatedFTE += metrics.allocatedFTE;
  actionRole.metrics[1].fte += metrics.fte;
  actionRole.metrics[1].hours += metrics.hours;
  actionRole.metrics[1].roles += 1;
  action[EntityType.ROLE].set(role.uuid, actionRole);

  // Individual role impact changes.
  actionRole.impact.budget = metrics.budget;
  actionRole.impact.allocatedFTE = metrics.allocatedFTE;
  actionRole.impact.fte = metrics.fte;
  actionRole.impact.hours = metrics.hours;

  const roleCount = action[EntityType.ROLE].size;
  const groupCount = action[EntityType.GROUP].size;

  // Role impact changes.
  action.impact.roles = roleCount;
  actionRole.impact.roles = roleCount;

  const roleLabel = entityLabel(EntityType.ROLE, roleCount !== 1);
  const groupLabel = entityLabel(EntityType.GROUP, groupCount !== 1);
  const verb = roleCount === 1 ? 'was' : 'were';
  const adverb = groupCount === 1 ? ', impacting' : ' across';

  action.title = `${roleCount} ${roleLabel} ${verb} added`;

  if (groupCount > 0) {
    action.title = `${action.title}${adverb} ${groupCount} ${groupLabel}`;
  }

  return action;
}
